import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = '关于我们 | 体育App指南';
  }, []);

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const formUrl = 'https://readdy.ai/api/form/d9tcb7rpv18futdmib90';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) return;

    const form = e.currentTarget;
    const hpField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (hpField && hpField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed = null;
      try { parsed = JSON.parse(responseText); } catch (_e) { /* ignore */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '体育App指南',
        url: 'https://pvqqe.cn',
        description: '致力于为中国体育爱好者提供最专业、最全面的体育App推荐与下载指南。',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@tiyuappguide.com',
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 体育App指南',
        url: 'https://pvqqe.cn/about',
        isPartOf: { '@id': 'https://pvqqe.cn#website' },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium whitespace-nowrap">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">关于我们</h1>
            <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              体育App指南致力于为中国体育爱好者提供最专业、最全面的体育App推荐与下载指南
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">
                  品牌介绍
                </h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  <p>
                    体育App指南成立于2024年，是一个专注于体育类移动应用推荐与评测的专业平台。我们深知在数以万计的体育App中找到最适合自己的那一款并不容易，因此我们致力于通过深度评测、横向对比和使用攻略，帮助每一位体育爱好者做出明智的选择。
                  </p>
                  <p>
                    我们的团队由资深体育迷和移动互联网从业者组成，每一位成员都对体育充满热情，并对移动应用有着深刻的理解。我们坚持以用户体验为核心，从赛事版权、功能特性、界面设计、性价比等多个维度对每一款App进行客观评估。
                  </p>
                  <p>
                    无论你是狂热的足球迷、忠实的NBA观众，还是热爱跑步、健身的运动达人，体育App指南都能为你提供有价值的参考信息。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">
                  我们的理念
                </h2>
                <div className="space-y-3">
                  {values.map((v) => (
                    <div key={v.title} className="flex gap-4 p-4 bg-background-100 rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                        <i className={`${v.icon} text-accent-500 text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground-950 text-sm mb-1">{v.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl font-bold text-foreground-950 mb-3 font-heading">用户口碑</h2>
              <p className="text-sm text-foreground-600">来自用户的真实反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.map((r) => (
                <div key={r.name} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-xs ${i < r.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{r.text}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl font-bold text-foreground-950 mb-3 font-heading">联系我们</h2>
              <p className="text-sm text-foreground-600">有任何问题或建议，欢迎随时联系我们</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm text-foreground-950 font-medium">contact@tiyuappguide.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-secondary-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm text-foreground-950 font-medium">体育App指南</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">新浪微博</p>
                    <p className="text-sm text-foreground-950 font-medium">@体育App指南</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  id="contact-form"
                  data-readdy-form=""
                  action={formUrl}
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="您的姓名"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-accent-300 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="您的邮箱"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-accent-300 transition-colors"
                  />
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请输入您的留言（500字以内）"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-accent-300 transition-colors resize-none"
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-hp"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-accent-500 hover:bg-accent-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-xs text-accent-600 text-center">留言发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-xs text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </>
  );
}

const values = [
  { icon: 'ri-shield-check-line', title: '客观公正', desc: '每一篇评测都基于真实数据和实际使用体验，不接受付费软文，保持评测的独立性和客观性。' },
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为出发点，提供有价值的推荐和实用的使用攻略，帮助用户解决实际问题。' },
  { icon: 'ri-lightbulb-line', title: '持续更新', desc: '体育App市场日新月异，我们持续追踪行业动态，及时更新推荐内容和评测信息。' },
];

const reviews = [
  { name: '李**', text: '作为一个篮球迷，一直不知道哪个直播App最好用。看了体育App指南的对比文章后，终于找到了最适合我的App，感谢！', rating: 5 },
  { name: '王**', text: '非常专业的体育App推荐平台，每篇文章都写得很详细。帮我省了很多试错时间，已经推荐给跑团的朋友们了。', rating: 5 },
  { name: '张**', text: '分类很清晰，想找什么类型的体育App都能快速定位。评测也很客观，没有一味吹捧某个App。', rating: 4 },
];